// src/integrations/APIClient.js
import { auth } from "../components/firebase";

const ENV_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "")
  .trim()
  .replace(/\/+$/, "");

const DEFAULT_REMOTE_API_BASE_URL = "https://chesshood.com";

export const BASE_URL = ENV_BASE_URL || DEFAULT_REMOTE_API_BASE_URL;

function buildApiUrl(path) {
  return BASE_URL ? new URL(path, BASE_URL).href : path;
}

const TRAINING_AZURE_ORIGIN = "https://chesshoodbackend.azurewebsites.net";

function isTrainingApiPath(pathname) {
  return pathname === "/api/training" ||
    pathname.startsWith("/api/training-payments/") ||
    pathname.startsWith("/api/puzzles/");
}

async function apiFetch(path, options = {}) {
  const url = buildApiUrl(path);
  const parsed = new URL(url);
  const canUseTrainingFallback =
    isTrainingApiPath(parsed.pathname) && parsed.origin !== TRAINING_AZURE_ORIGIN;
  const backupUrl = `${TRAINING_AZURE_ORIGIN}${parsed.pathname}${parsed.search}`;
  const isRead = !options.method || options.method.toUpperCase() === "GET";

  try {
    const result = await fetch(url, options);
    const responseType = (result.headers.get("content-type") || "").toLowerCase();
    // An unknown API route sometimes gets SPA index.html (HTTP 200), not JSON.
    // Missing routes may retry on our first-party Azure backend only.
    const routeMissing = result.status === 404 || result.status === 405 ||
      (result.ok && responseType.includes("text/html"));
    if (canUseTrainingFallback && routeMissing) {
      return await fetch(backupUrl, options);
    }
    return result;
  } catch (error) {
    if (canUseTrainingFallback && isRead && error.name !== "AbortError") {
      try { return await fetch(backupUrl, options); }
      catch (backupError) { error = backupError; }
    }
    const networkError = new Error(
      `Failed to fetch ${url}. Check the API connection and server configuration.`
    );
    networkError.cause = error;
    throw networkError;
  }
}

async function handleResponse(response) {
  if (!response.ok) {
    const errText = await response.text().catch(() => "");
    throw new Error(errText || response.statusText);
  }

  if (response.status === 204) {
    return null;
  }

  const ct = response.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return response.json();
  }

  const text = await response.text().catch(() => "");
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function authHeader(headers = new Headers()) {
  const currentUser = auth.currentUser;
  if (currentUser) {
    const token = await currentUser.getIdToken();
    headers.append("Authorization", `Bearer ${token}`);
  }
  return headers;
}

function buildUrlWithParams(path, params = {}) {
  const base = BASE_URL || window.location.origin;
  const url = new URL(path, base);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.append(key, value);
    }
  });

  return BASE_URL ? url.href : `${url.pathname}${url.search}`;
}

/* ------------------------- USERS ------------------------- */

export async function getUser() {
  try {
    const response = await apiFetch("/api/user", {
      headers: await authHeader(),
    });
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching user:", error);
    throw error;
  }
}

export async function updateUser(userDetails) {
  const headers = new Headers();
  headers.append("Content-Type", "application/json");
  headers.append("Authorization", `Bearer ${userDetails.token}`);

  const { token, ...userPayload } = userDetails;

  const response = await apiFetch("/api/user", {
    method: "POST",
    headers,
    body: JSON.stringify(userPayload),
  });

  return handleResponse(response);
}

export async function createStripeCheckoutSession(payload) {
  try {
    const headers = await authHeader();
    headers.append("Content-Type", "application/json");

    const response = await apiFetch("/api/stripe/create-checkout-session", {
      method: "POST",
      headers,
      body: JSON.stringify(payload || {}),
    });

    return handleResponse(response);
  } catch (error) {
    console.error("Error creating Stripe checkout session:", error);
    throw error;
  }
}

/* ------------------------ TRAINING ------------------------ */

export async function getFirstPuzzle(lang, extraParams = {}) {
  try {
    const paidId = new URLSearchParams(window.location.search).get("session");
    if (paidId) {
      const headers = await authHeader();
      headers.set("X-Training-Session", paidId);
      return handleResponse(await apiFetch(buildUrlWithParams("/api/training-payments/puzzle", { lang, ...extraParams }), { headers }));
    }
    const response = await apiFetch(
      buildUrlWithParams("/api/training", { lang, ...extraParams }),
      {
        headers: await authHeader(),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching the first puzzle:", error);
    throw error;
  }
}

export async function getPuzzle(puzzleid, category, lang, extraParams = {}) {
  try {
    const paidId = new URLSearchParams(window.location.search).get("session");
    if (paidId) {
      const headers = await authHeader();
      headers.set("X-Training-Session", paidId);
      return handleResponse(await apiFetch(buildUrlWithParams("/api/training-payments/puzzle", { lang, puzzleId: puzzleid, ...extraParams }), { headers }));
    }
    const path = puzzleid ? `/api/puzzles/${puzzleid}` : "/api/puzzles/";
    const response = await apiFetch(
      buildUrlWithParams(path, { category, lang, ...extraParams }),
      {
        headers: await authHeader(),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching puzzle:", error);
    throw error;
  }
}

/* ------------------------- STATS ------------------------- */

export async function getUserSuccesses() {
  try {
    const response = await apiFetch("/api/user/successes", {
      headers: await authHeader(),
    });
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching user successes:", error);
    throw error;
  }
}

export async function getUserFailures(category) {
  try {
    const response = await apiFetch(
      `/api/user/failures/${encodeURIComponent(category)}`,
      {
        headers: await authHeader(),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching user failures:", error);
    throw error;
  }
}

export async function getTotalUserFailures() {
  try {
    const response = await apiFetch("/api/user/failures", {
      headers: await authHeader(),
    });
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching total user failures:", error);
    throw error;
  }
}

export async function getTotalUserHints() {
  try {
    const response = await apiFetch("/api/user/hints_count", {
      headers: await authHeader(),
    });
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching total user hints:", error);
    throw error;
  }
}

/* ------------------------- EDITOR ------------------------- */

export async function savePuzzle(puzzleid, updatedPuzzle) {
  try {
    const headers = await authHeader();
    headers.append("Content-Type", "application/json");

    const response = await apiFetch(`/api/edit/${puzzleid}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(updatedPuzzle),
    });
    return handleResponse(response);
  } catch (error) {
    console.error("Error saving puzzle:", error);
    throw error;
  }
}

export async function getPuzzlesByCategory(
  category,
  page,
  size,
  lang,
  extraParams = {}
) {
  try {
    const response = await apiFetch(
      buildUrlWithParams(
        `/api/puzzles/category/${encodeURIComponent(category)}`,
        {
          lang,
          ...extraParams,
        }
      ),
      {
        headers: await authHeader(),
      }
    );
    const payload = await handleResponse(response);

    if (!Array.isArray(payload)) {
      return payload;
    }

    const normalizedPage = Number.isFinite(Number(page)) ? Math.max(0, Number(page)) : 0;
    const normalizedSize = Number.isFinite(Number(size)) ? Math.max(0, Number(size)) : 0;

    if (!normalizedSize) {
      return payload;
    }

    const startIndex = normalizedPage * normalizedSize;
    return payload.slice(startIndex, startIndex + normalizedSize);
  } catch (error) {
    console.error("Error fetching puzzles by category:", error);
    throw error;
  }
}

/* -------------------------- CHECK ------------------------- */

export async function checkPuzzleResult(puzzleid, pgn, hint, lang) {
  try {
    const headers = await authHeader();
    headers.append("Content-Type", "application/json");

    const response = await apiFetch(`/api/puzzles/${puzzleid}/check`, {
      method: "POST",
      headers,
      body: JSON.stringify({ pgn, after_pgn: pgn, hint, lang }),
    });
    return handleResponse(response);
  } catch (error) {
    console.error("Error checking puzzle result:", error);
    throw error;
  }
}

/* ----------------------- MISC QUERIES ---------------------- */

export async function getLast100Puzzles(userId) {
  try {
    const response = await apiFetch(
      `/api/user/${encodeURIComponent(userId)}/last100puzzles`,
      {
        headers: await authHeader(),
      }
    );
    return handleResponse(response);
  } catch (error) {
    console.error("Error fetching last 100 puzzles:", error);
    throw error;
  }
}

export async function getCategoryInfo(category, lang) {
  const headers = await authHeader();

  try {
    const response = await apiFetch(
      buildUrlWithParams(`/api/categories/${encodeURIComponent(category)}`, {
        lang,
      }),
      { headers }
    );
    return handleResponse(response);
  } catch (error) {
    try {
      const fallbackResponse = await apiFetch(
        buildUrlWithParams(
          `/api/categoriesPuzzles/${encodeURIComponent(category)}`,
          { lang }
        ),
        { headers }
      );
      return handleResponse(fallbackResponse);
    } catch (fallbackError) {
      console.error(`Error fetching category info for ${category}:`, fallbackError);
      throw fallbackError;
    }
  }
}

